/**
 * FLX — bespoke landing (Layer-3 tenant override of `@app/storefront/tenant-landing`).
 *
 * PORTED FROM THE CLIENT'S LIVE SITE
 * (https://flx-website-production.up.railway.app/). The markup below is that
 * page's structure and class names; `flx.css` is that page's stylesheet, scoped.
 * When something needs to change, fetch the live page and read it — do NOT
 * re-derive from a design recreation. An earlier pass did exactly that, from a
 * handoff whose own author had noted the site's CSS "was not readable (no CORS,
 * no public stylesheet path)" and had guessed; the guess was wrong on the
 * typeface, the primary colour, the section backgrounds and the whole hero.
 *
 * ONE THING THIS PAGE DOES THAT THE LIVE SITE DOESN'T: it sells. Section 02's
 * flavour cards are real catalog rows (`shop/spec-card.tsx`), carrying a price
 * and an Add to Cart that feed the drawer + checkout overlay mounted in the
 * shell. The live site is pre-launch and has no commerce at all. The generic
 * `/storefront` marketplace redirects back into this page for this tenant.
 *
 * The seam `home.tsx` / `App.tsx` consume is FOUR exports — `HAS_BESPOKE_LANDING`,
 * `TenantLanding`, `renderTenantPage`, `TENANT_ROUTES`. They are NOT optional: a
 * tenant folder that omits any breaks THIS tenant's build at the
 * `@app/storefront/tenant-landing` import (`tsc --noEmit`).
 */
/* eslint-disable react-refresh/only-export-components --
 * Two of those four seam exports are values, not components, so this file can
 * never satisfy the rule — the same warning stands on every tenant's seam file.
 * Fast-refresh granularity is the only cost. */
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { FlxShell, scrollToAnchor } from './shell';
import { Hero } from './hero';
import { RitualVisual } from './art';
import { ImageSlot } from './interactive';
import { EarlyAccessForm } from './early-access';
import { FlxCatalogGrid } from './shop/buy-section';
import { FlxFlavourShelf } from './shop/spec-card';
import {
  AUDIENCES,
  BANNER,
  BIGNUMS,
  CERTS,
  COA,
  COACHES,
  CULTURE_FLX,
  CULTURE_OLD,
  EARLY_ACCESS,
  ECOSYSTEM,
  ECOSYSTEM_CARDS,
  FAQS,
  FAQ_HEAD,
  FREEOF,
  IMG,
  INGREDIENTS,
  INSIDE,
  MARQUEE,
  PHOTO_BAND,
  RITUAL,
  SCIENCE,
  STANDARDS,
  STEPS,
  TRUST,
  USE_SAFETY,
  WHO,
} from './tokens';

export const HAS_BESPOKE_LANDING = true;

export function TenantLanding(): JSX.Element {
  return (
    <FlxShell>
      <FlxHome />
    </FlxShell>
  );
}

/**
 * `/storefront` folds back into the landing's own shop.
 *
 * The route stays MOUNTED (App.tsx is tenant-agnostic) — this just makes it a
 * redirect for the FLX build, which is also what catches the portal sidebar's
 * "Store" link and the legacy `/store` redirects without touching shared code.
 * `/cart`, `/checkout` and `/product/:id` are deliberately left alone: they are
 * the fallback for a variation pick or a resumed payment.
 */
export function renderTenantPage(path: string): JSX.Element | null {
  if (path === '/storefront') return <Navigate to="/#inside" replace />;
  return null;
}
export const TENANT_ROUTES: string[] = [];

const INK_LINE = { borderColor: 'rgba(10,10,10,0.15)' };
const INK_LEDE = { color: 'rgba(10,10,10,0.72)' };

/**
 * Reveal-on-scroll. The live site adds `.in` to every `.reveal` via one
 * IntersectionObserver; the CSS does the rest. Under reduced motion everything is
 * revealed immediately rather than animated in.
 */
function useReveal(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = Array.from(root.querySelectorAll<HTMLElement>('.reveal'));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ref]);
}

/** Section header: `NN / Label` + two-line headline + right-aligned lede. */
function SecHead({
  num,
  title,
  lede,
  onInk = true,
  style,
}: {
  num: string;
  title: readonly [string, string];
  lede: string;
  onInk?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div className="sec-head reveal" style={{ ...(onInk ? INK_LINE : {}), ...style }}>
      <div>
        <div className="num">{num}</div>
        <h2>
          {title[0]}
          <br />
          {title[1]}
        </h2>
      </div>
      <p className="lede" style={onInk ? INK_LEDE : undefined}>
        {lede}
      </p>
    </div>
  );
}

/** The orange-ruled aside used for every "in development / note" block. */
function FutureNote({
  label,
  children,
  labelColor = 'var(--orange)',
  style,
}: {
  label: string;
  children: React.ReactNode;
  labelColor?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className="future-note reveal" style={style}>
      <span className="micro" style={{ color: labelColor }}>
        {label}
      </span>
      {children}
    </div>
  );
}

/** Looping phrase ticker. The track is the list twice over so the CSS 50%
 * translate loops seamlessly; decorative, so it's hidden from assistive tech. */
function Marquee() {
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee__track">
        {[...MARQUEE, ...MARQUEE].map((m, i) => (
          <span key={`${m}-${i}`}>{m}</span>
        ))}
      </div>
    </div>
  );
}

// ── 01 Why Creatine + Electrolytes ───────────────────────────────────────────
function Science() {
  return (
    <section id="science" className="sec sec--paper">
      <div className="container">
        <SecHead num={SCIENCE.kicker} title={SCIENCE.title} lede={SCIENCE.lede} />
        <div className="science-grid">
          <div className="science-lead reveal">
            <p>{SCIENCE.body}</p>
            <div className="pull" style={{ borderColor: 'var(--orange)' }}>
              {SCIENCE.callout[0]}
              <br />
              {SCIENCE.callout[1]} <span className="accent">{SCIENCE.callout[2]}</span>
            </div>
            <p>{SCIENCE.calloutBody}</p>
          </div>
          <div className="bignum-grid reveal">
            {BIGNUMS.map((b) => (
              <div className="bignum reveal" key={b.k}>
                <div className="v">
                  {b.v}
                  <small>{b.sup}</small>
                </div>
                <div className="k">{b.k}</div>
                <div className="d">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── 02 What's Inside — the catalog-driven flavour shelf ──────────────────────
function Inside() {
  return (
    <section id="inside" className="sec sec--paper" style={{ padding: 0 }}>
      <div className="container" style={{ paddingTop: 120, paddingBottom: 56 }}>
        <SecHead
          num={INSIDE.kicker}
          title={INSIDE.title}
          lede={INSIDE.lede}
          style={{ marginBottom: 0 }}
        />
      </div>

      <FlxFlavourShelf />

      <div className="container">
        <FutureNote label={INSIDE.moreFlavours.label} style={{ marginTop: 40 }}>
          <p>{INSIDE.moreFlavours.body}</p>
        </FutureNote>
      </div>

      {/* The rest of the house range (kits, gear). Renders nothing while the
       * catalog is just the two launch pouches, and appears on its own the day
       * it isn't — so the MLM side stays sellable without a second redesign. */}
      <div className="container">
        <FlxCatalogGrid />
      </div>

      {/* Shared supplement facts */}
      <div className="container specs-panel reveal">
        <div className="specs-head">
          <h3>
            {INSIDE.dosedTitle[0]}
            <br />
            {INSIDE.dosedTitle[1]}
          </h3>
          <p>{INSIDE.dosedBody}</p>
          <div className="specs-sticks" aria-hidden="true">
            <ImageSlot src={IMG.stickOrange} label="" className="ss-1" />
            <ImageSlot src={IMG.stickLemon} label="" className="ss-2" />
          </div>
        </div>
        <div className="specs-rows">
          {INGREDIENTS.map((ing) => (
            <div className="ic-row reveal" key={ing.n}>
              <div className="n">{ing.n}</div>
              <div>
                <h4>
                  {ing.h}
                  {ing.tag && <span className="mesh-tag">{ing.tag}</span>}
                </h4>
                <p>{ing.p}</p>
              </div>
              <div className="amt">
                {ing.a}
                <small>{ing.u}</small>
              </div>
            </div>
          ))}
          <div className="freeof reveal">
            {FREEOF.map((f) => (
              <span className="chip" key={f}>
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="container reveal" style={{ marginTop: 32 }}>
        <FutureNote label={COA.h} style={{ marginTop: 0 }}>
          <p>{COA.p}</p>
        </FutureNote>
      </div>

      <div className="container reveal" style={{ marginTop: 20 }}>
        <FutureNote
          label={USE_SAFETY.h}
          labelColor="var(--mute)"
          style={{ marginTop: 0, borderLeftColor: 'rgba(10,10,10,0.28)' }}
        >
          <p>
            <strong>Directions:</strong> {USE_SAFETY.directions}
          </p>
          <p style={{ marginTop: 10 }}>
            <strong>Caution:</strong> {USE_SAFETY.caution}
          </p>
          <p
            style={{
              marginTop: 12,
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              fontSize: 12,
              color: 'var(--black)',
            }}
          >
            No Approved Therapeutic Claims
          </p>
          <p style={{ marginTop: 6, fontSize: 12, color: 'rgba(10,10,10,0.55)' }}>
            <em>{INSIDE.doh}</em>
          </p>
        </FutureNote>
      </div>
    </section>
  );
}

// ── 03 Who It's For ──────────────────────────────────────────────────────────
function Who() {
  return (
    <section id="who" className="sec sec--paper">
      <div className="container">
        <SecHead num={WHO.kicker} title={WHO.title} lede={WHO.lede} onInk={false} />
        <div className="who-grid reveal">
          {AUDIENCES.map((a) => (
            <article className="who-card" key={a.n}>
              <div className="who-card__bar" />
              <div className="who-card__no">{a.n}</div>
              <h3>{a.h}</h3>
              <p>{a.p}</p>
              <div className="who-card__tags">
                {a.chips.map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
        <FutureNote label={WHO.callout.label}>
          <p>{WHO.callout.body}</p>
        </FutureNote>
      </div>
    </section>
  );
}

// ── 04 The Daily Ritual ──────────────────────────────────────────────────────
function Ritual() {
  return (
    <section id="ritual" className="sec sec--paper">
      <div className="container">
        <SecHead num={RITUAL.kicker} title={RITUAL.title} lede={RITUAL.lede} />
        <div className="ritual">
          {STEPS.map((s, i) => (
            <div className="ritual-step reveal" key={s.n}>
              <div className="stp">{s.n}</div>
              <div className="visual">
                <RitualVisual step={i} />
              </div>
              <h4>{s.h}</h4>
              <p>{s.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── 05 Our Trust Standards ───────────────────────────────────────────────────
function Trust() {
  return (
    <section id="trust" className="sec sec--paper">
      <div className="container">
        <SecHead num={TRUST.kicker} title={TRUST.title} lede={TRUST.lede} onInk={false} />
        <div className="trust-grid reveal">
          {STANDARDS.map((s) => (
            <div className="trust-card" key={s.n}>
              <div className="tn">{s.n}</div>
              <h4>{s.h}</h4>
              <p>{s.p}</p>
            </div>
          ))}
        </div>

        <div className="testing-banner reveal">
          <div className="tb-badge micro" style={{ color: 'var(--orange)' }}>
            {TRUST.callout.label}
          </div>
          <p>{TRUST.callout.body}</p>
          <a
            href={TRUST.callout.cta.href}
            className="btn btn--ghost"
            onClick={scrollToAnchor(TRUST.callout.cta.href)}
          >
            {TRUST.callout.cta.label} <span className="arrow">→</span>
          </a>
        </div>

        <div className="certs reveal">
          <div className="certs-label">
            <span className="micro">{TRUST.certKicker}</span>
            <h3>
              {TRUST.certTitle[0]} {TRUST.certTitle[1]}
            </h3>
            <p>{TRUST.certLede}</p>
          </div>
          <div className="certs-row">
            {CERTS.map((c) => (
              <figure className="cert" key={c.caption}>
                <ImageSlot src={c.src} label={c.alt} />
                <figcaption>{c.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Culture band (team photo + #BeTheEnergy) ─────────────────────────────────
function CultureBand() {
  return (
    <section className="culture-band" aria-label="The FLX team — Be The Energy">
      <div className="container">
        <div className="cb-inner reveal">
          <div className="hero__kicker">
            <span className="bar" />
            {BANNER.tag}
          </div>
          <h2>
            {BANNER.title[0]}
            <br />
            {BANNER.title[1]}
          </h2>
          <p>{BANNER.body}</p>
          <a
            href={BANNER.cta.href}
            className="btn btn--primary"
            onClick={scrollToAnchor(BANNER.cta.href)}
          >
            {BANNER.cta.label} <span className="arrow">→</span>
          </a>
        </div>
      </div>
    </section>
  );
}

// ── 06 Coach-Led Culture ─────────────────────────────────────────────────────
function Coaches() {
  return (
    <section id="coaches" className="sec sec--paper coach-section">
      <div className="container">
        <SecHead num={COACHES.kicker} title={COACHES.title} lede={COACHES.lede} onInk={false} />
        <div className="coach-compare reveal">
          {[
            { d: CULTURE_OLD, accent: false },
            { d: CULTURE_FLX, accent: true },
          ].map(({ d, accent }) => (
            <div
              className="coach-col"
              key={d.title}
              style={accent ? { borderColor: 'var(--orange)' } : undefined}
            >
              <div
                className="label"
                style={accent ? { color: 'var(--orange)', opacity: 1 } : undefined}
              >
                {d.eyebrow}
              </div>
              <h3>{d.title}</h3>
              <p>{d.lead}</p>
              <ul>
                {d.points.map((pt) => (
                  <li key={pt}>{pt}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="testing-banner reveal" style={{ borderColor: 'var(--line)' }}>
          <div className="tb-badge micro" style={{ color: 'var(--orange)' }}>
            {COACHES.callout.label}
          </div>
          <p>{COACHES.callout.body}</p>
          <a
            href={COACHES.callout.cta.href}
            className="btn btn--primary"
            onClick={scrollToAnchor(COACHES.callout.cta.href)}
          >
            {COACHES.callout.cta.label} <span className="arrow">→</span>
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Faces band ───────────────────────────────────────────────────────────────
function FacesBand() {
  return (
    <section className="faces-band sec--dark" aria-label="The people of FLX">
      <div className="container">
        <div className="faces-head reveal">
          <span className="micro accent">{PHOTO_BAND.tag}</span>
          <h2>
            {PHOTO_BAND.title[0]}
            <br />
            {PHOTO_BAND.title[1]}
          </h2>
          <p>{PHOTO_BAND.body}</p>
          <p
            className="micro"
            style={{ color: 'rgba(255,255,255,0.55)', letterSpacing: '0.3em', marginTop: 8 }}
          >
            {PHOTO_BAND.meta}
          </p>
        </div>
        <div className="faces-row">
          <figure className="face reveal">
            <ImageSlot src={IMG.coach} label="A member of the FLX community wearing FLX" />
          </figure>
        </div>
      </div>
    </section>
  );
}

// ── 07 The FLX Ecosystem ─────────────────────────────────────────────────────
function Ecosystem() {
  return (
    <section id="ecosystem" className="sec sec--paper">
      <div className="container">
        <SecHead num={ECOSYSTEM.kicker} title={ECOSYSTEM.title} lede={ECOSYSTEM.lede} />
        <div className="eco-grid reveal">
          {ECOSYSTEM_CARDS.map((c) => (
            <div className="eco-card" key={c.n}>
              <div className="eco-num">{c.n}</div>
              <h4>{c.h}</h4>
              <p>{c.p}</p>
            </div>
          ))}
        </div>
        <FutureNote label={ECOSYSTEM.callout.label} style={{ marginTop: 40 }}>
          <p>{ECOSYSTEM.callout.body}</p>
        </FutureNote>
      </div>
    </section>
  );
}

// ── 08 Get Involved (early access) ───────────────────────────────────────────
function Check() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M13.5 4.5L6 12 2.5 8.5"
        stroke="#F25C05"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EarlyAccess() {
  return (
    <section id="early-access" className="buy-section">
      <div className="container">
        <div className="buy-grid">
          <div className="buy-hero-text reveal">
            <h2>
              {EARLY_ACCESS.pitch[0]}
              <br />
              <span className="accent">{EARLY_ACCESS.pitch[1]}</span>
              <br />
              {EARLY_ACCESS.pitch[2]}
              <br />
              {EARLY_ACCESS.pitch[3]}
            </h2>
            <p>{EARLY_ACCESS.pitchBody}</p>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {EARLY_ACCESS.bullets.map((b) => (
                <div
                  key={b}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13,
                    opacity: 0.55,
                  }}
                >
                  <Check />
                  {b}
                </div>
              ))}
            </div>
          </div>
          <div className="reveal">
            <EarlyAccessForm />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ──────────────────────────────────────────────────────────────────────
function Faq() {
  const [open, setOpen] = useState<number | null>(null);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  return (
    <section id="faq" className="faq-section">
      <div className="container">
        <SecHead num={FAQ_HEAD.kicker} title={FAQ_HEAD.title} lede={FAQ_HEAD.lede} />
        <div className="faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div className={`faq-item${isOpen ? ' open' : ''}`} key={f.q}>
                <div
                  className="faq-q"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setOpen(isOpen ? null : i);
                    }
                  }}
                >
                  {f.q}
                  <div className="faq-icon">+</div>
                </div>
                <div
                  className="faq-a"
                  ref={(el) => {
                    refs.current[i] = el;
                  }}
                  style={{ maxHeight: isOpen ? (refs.current[i]?.scrollHeight ?? 800) : 0 }}
                >
                  <div className="faq-a-inner">{f.a}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Land on the section named by `#hash`.
 *
 * An in-page `<a href="#inside">` is handled by `scrollToAnchor`, but arriving
 * from ANOTHER route is not — `/storefront` redirects here as `/#inside`, as does
 * the portal sidebar's "Store" link. The browser can't do it itself: at mount the
 * target sits below a 220vh hero and has no layout yet, so this waits a frame.
 */
function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || hash === '#top') return;
    const id = hash.slice(1);
    const raf = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [hash]);
}

function FlxHome(): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  useReveal(rootRef);
  useHashScroll();
  return (
    <div ref={rootRef}>
      <Hero />
      <Marquee />
      <Science />
      <Inside />
      <Who />
      <Ritual />
      <Trust />
      <CultureBand />
      <Coaches />
      <FacesBand />
      <Ecosystem />
      <EarlyAccess />
      <Faq />
    </div>
  );
}
